import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useOfflineMode } from '@/hooks/useOfflineMode';
import { offlineCache } from '@/lib/offlineCache';
import { logger } from '@/utils/logger';
import { toError } from '@/utils/toError';

interface OfflineContextType {
  isOnline: boolean;
  isOffline: boolean;
  hasOfflineData: boolean;
  isSyncing: boolean;
  syncOfflineData: () => Promise<void>;
  cacheData: (type: string, data: any) => Promise<void>;
  getCachedData: (type: string) => Promise<any[]>;
  addToOfflineQueue: (type: string, data: any) => void;
}

const OfflineContext = createContext<OfflineContextType | undefined>(undefined);

interface OfflineProviderProps {
  children: ReactNode;
}

export const OfflineProvider: React.FC<OfflineProviderProps> = ({ children }) => {
  const { isOnline, isOffline, addToOfflineQueue, hasOfflineData, isSyncing, syncOfflineData } = useOfflineMode();

  useEffect(() => {
    // Initialize IndexedDB
    offlineCache.init().catch(err => { if (import.meta.env.DEV) logger.error('IndexedDB init error:', toError(err).message); });
    
    // Cleanup old cache items on app start
    offlineCache.cleanupOldItems().catch(err => { if (import.meta.env.DEV) logger.error('Cache cleanup error:', toError(err).message); });
  }, []);

  const cacheData = async (type: string, data: any) => {
    try {
      switch (type) {
        case 'flashcard':
          await offlineCache.cacheFlashcard(data);
          break;
        case 'clinical_case':
          await offlineCache.cacheClinicalCase(data);
          break;
        case 'calculator':
          await offlineCache.cacheCalculator(data);
          break;
        default:
          if (import.meta.env.DEV) logger.warn('Unknown cache type:', type);
      }
    } catch (error) {
      if (import.meta.env.DEV) logger.error('Failed to cache data:', toError(error).message);
    }
  };

  const getCachedData = async (type: string) => {
    try {
      switch (type) {
        case 'flashcards':
          return await offlineCache.getCachedFlashcards();
        case 'clinical_cases':
          return await offlineCache.getCachedClinicalCases();
        case 'calculators':
          return await offlineCache.getCachedCalculators();
        default:
          return [];
      }
    } catch (error) {
      if (import.meta.env.DEV) logger.error('Failed to get cached data:', toError(error).message);
      return [];
    }
  };

  const handleAddToOfflineQueue = (type: string, data: any) => {
    addToOfflineQueue({ type: type as any, data });
  };

  const value: OfflineContextType = {
    isOnline,
    isOffline,
    hasOfflineData,
    isSyncing,
    syncOfflineData,
    cacheData,
    getCachedData,
    addToOfflineQueue: handleAddToOfflineQueue
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (context === undefined) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};